import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import PageSeo from "components/Seo/PageSeo";
import { truncateDescription } from "helpers/seo/siteConfig";
import { useDispatch, useSelector } from "react-redux";
import { fetchPrograms } from "helpers/jackrabbit/fetchPrograms";
import {
  DEFAULT_CLASS_SCHEDULE_FILTER,
  filterAndSortClassRows,
  isClassScheduleFilterActive,
  summarizeFilterResults,
} from "helpers/jackrabbit/classScheduleFilter";
import {
  getAgeMismatchContext,
  getFilterEmptyReason,
  formatProgramAgeLabelFromRows,
} from "helpers/jackrabbit/programAgeBounds";
import { parseProgramDescription } from "helpers/jackrabbit/parseProgramDescription";
import { summarizeClassUrgency } from "helpers/jackrabbit/classUrgency";
import {
  parseScheduleFilterSearchParams,
  SCHEDULE_FILTER_HASH,
} from "helpers/jackrabbit/scheduleFilterSearchParams";
import { setSelectedProgram } from "store/actions/navigationActions";
import ProgramDetailCard from "components/ProgramDetailCard/programDetailCard";
import ClassScheduleFilter from "components/ProgramPage/ClassScheduleFilter";
import ProgramsFilterEmpty from "components/ProgramPage/ProgramsFilterEmpty";
import ProgramPageHero from "components/ProgramPage/ProgramPageHero";
import ProgramSectionHeader from "components/ProgramPage/ProgramSectionHeader";
import ProgramDescriptionBlock from "components/ProgramPage/ProgramDescriptionBlock";
import { trackScheduleFilterResults, trackScheduleFilterFirstUse } from "helpers/analytics/trackScheduleFilter";
import { resolveProgramPageContent } from "helpers/contentful/resolveProgramPageContent";
import useDebouncedScheduleFilter from "hooks/useDebouncedScheduleFilter";
import "components/Home/homeShared.css";
import "components/ProgramPage/programsPage.css";

const PROGRAMS_WITHOUT_SCHEDULE_FILTER = new Set(["Competitive"]);

const ProgramDescription = () => {
  const { programtype } = useParams();
  const [searchParams] = useSearchParams();
  const programTitles = useSelector(
    (state) => state.contentfulReducer.programTitles
  );
  const dispatch = useDispatch();
  const [scheduleFilter, setScheduleFilter] = useState(
    DEFAULT_CLASS_SCHEDULE_FILTER
  );
  const { settledFilter, isFilterSettling } = useDebouncedScheduleFilter(
    scheduleFilter,
    { resetKey: `${programtype}:${searchParams.toString()}` }
  );
  const [sectionResults, setSectionResults] = useState({});

  const currentProgram = programTitles.find(
    (element) => element.fields.displayName === programtype
  );

  const pageTitle = useMemo(() => {
    if (!currentProgram) return "Program";
    const name = currentProgram.fields.displayName;
    return name === "Tumbling" ? name : `${name} Gymnastics`;
  }, [currentProgram]);

  const heroImage = currentProgram?.fields?.image?.fields?.file?.url;
  const cat2Sections = useMemo(
    () => currentProgram?.fields?.jackrabbitCat2 ?? [],
    [currentProgram?.fields?.jackrabbitCat2]
  );
  const showScheduleFilter = !PROGRAMS_WITHOUT_SCHEDULE_FILTER.has(programtype);

  useEffect(() => {
    if (!currentProgram) return;
    if (programtype) dispatch(setSelectedProgram(programtype));
    fetchPrograms({ Cat1: currentProgram.fields.jackrabbitSlug });
    return () => {
      dispatch(setSelectedProgram(""));
    };
  }, [programtype, currentProgram, dispatch]);

  useEffect(() => {
    setSectionResults({});
    setScheduleFilter(parseScheduleFilterSearchParams(searchParams));
  }, [programtype, searchParams]);

  useEffect(() => {
    if (!searchParams.get("ageYears")) return undefined;

    const scrollToFilter = () => {
      const target = document.getElementById(SCHEDULE_FILTER_HASH);
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const timer = window.setTimeout(scrollToFilter, 250);
    return () => window.clearTimeout(timer);
  }, [programtype, searchParams]);

  const handleSectionResults = useCallback((sectionKey, results) => {
    setSectionResults((previous) => ({
      ...previous,
      [sectionKey]: results,
    }));
  }, []);

  const filterResults = useMemo(
    () => summarizeFilterResults(sectionResults),
    [sectionResults]
  );

  const hasLoadedSections = useMemo(() => {
    if (cat2Sections.length === 0) return true;

    return cat2Sections.every((cat2, index) => {
      const sectionKey = cat2.jackrabbitCat2Slug || String(index);
      const results = sectionResults[sectionKey];
      return results && !results.isLoading;
    });
  }, [cat2Sections, sectionResults]);

  const allProgramRows = useMemo(
    () =>
      Object.values(sectionResults).flatMap((section) =>
        section.isLoading ? [] : section.rows || []
      ),
    [sectionResults]
  );

  const pageContent = useMemo(() => {
    if (!currentProgram) {
      return { lead: "", highlights: [] };
    }

    const fields = currentProgram.fields;
    return resolveProgramPageContent({
      description: fields.description,
      overviewLead: fields.overviewLead,
      overviewHighlights: fields.overviewHighlights,
    });
  }, [currentProgram]);

  const programAgeLabel = useMemo(() => {
    if (!hasLoadedSections) return null;

    const jackrabbitLabel = formatProgramAgeLabelFromRows(allProgramRows);
    if (jackrabbitLabel) return jackrabbitLabel;

    if (!currentProgram?.fields?.description) return null;
    return parseProgramDescription(currentProgram.fields.description).ageLabel;
  }, [hasLoadedSections, allProgramRows, currentProgram]);

  const displayContent = useMemo(
    () => ({ ...pageContent, ageLabel: programAgeLabel }),
    [pageContent, programAgeLabel]
  );

  const isFilterActive =
    showScheduleFilter && isClassScheduleFilterActive(settledFilter);

  const ageMismatch = useMemo(
    () => getAgeMismatchContext(programtype, settledFilter, allProgramRows),
    [programtype, settledFilter, allProgramRows]
  );

  const filterEmptyReason = useMemo(() => {
    if (!hasLoadedSections) return null;
    return getFilterEmptyReason(programtype, settledFilter, allProgramRows);
  }, [programtype, settledFilter, allProgramRows, hasLoadedSections]);

  const filterUrgencySummary = useMemo(() => {
    if (!hasLoadedSections) return null;

    const matchedRows = Object.values(sectionResults).flatMap((section) => {
      if (section.isLoading || !section.rows?.length) return [];
      return filterAndSortClassRows(section.rows, settledFilter).rows;
    });

    if (matchedRows.length === 0) return null;
    return summarizeClassUrgency(matchedRows);
  }, [sectionResults, settledFilter, hasLoadedSections]);

  const showFilterEmpty =
    isFilterActive &&
    hasLoadedSections &&
    !isFilterSettling &&
    filterResults.matchCount === 0 &&
    filterResults.sourceCount > 0;

  const lastResultsTrackKeyRef = useRef("");
  const hasTrackedFirstFilterUseRef = useRef(false);

  useEffect(() => {
    if (!showScheduleFilter || !isFilterActive || isFilterSettling) return;
    if (hasTrackedFirstFilterUseRef.current) return;

    hasTrackedFirstFilterUseRef.current = true;
    trackScheduleFilterFirstUse(programtype, settledFilter);
  }, [
    showScheduleFilter,
    isFilterActive,
    isFilterSettling,
    programtype,
    settledFilter,
  ]);

  useEffect(() => {
    hasTrackedFirstFilterUseRef.current = false;
    lastResultsTrackKeyRef.current = "";
  }, [programtype]);

  useEffect(() => {
    if (
      !showScheduleFilter ||
      !isFilterActive ||
      !hasLoadedSections ||
      isFilterSettling
    ) {
      return;
    }

    const trackKey = JSON.stringify({
      filter: settledFilter,
      matchCount: filterResults.matchCount,
      sourceCount: filterResults.sourceCount,
    });

    if (lastResultsTrackKeyRef.current === trackKey) return;
    lastResultsTrackKeyRef.current = trackKey;

    trackScheduleFilterResults(
      programtype,
      filterResults,
      settledFilter,
      filterResults.matchCount === 0 ? filterEmptyReason : null
    );
  }, [
    showScheduleFilter,
    isFilterActive,
    hasLoadedSections,
    isFilterSettling,
    settledFilter,
    filterResults,
    filterEmptyReason,
    programtype,
  ]);

  if (programTitles.length === 0) {
    return (
      <p className="programs-cat-section__status programs-page__loading">
        Loading program information…
      </p>
    );
  }

  if (!currentProgram) {
    return (
      <div className="programs-not-found">
        <h1>Program not found</h1>
        <p>
          We could not find that program. Browse our{" "}
          <Link to="/programs/program-details/">programs</Link> to find the
          right class.
        </p>
      </div>
    );
  }

  const programDescription =
    currentProgram.fields.ProgramDescription ||
    currentProgram.fields.description ||
    "";
  const seoDescription = truncateDescription(
    `${currentProgram.fields.displayName} gymnastics classes at Ace Gymnastics in Bardstown, KY. ${programDescription}`
  );
  const programPath = `/programs/program-details/${encodeURIComponent(currentProgram.fields.displayName)}`;

  return (
    <>
      <PageSeo
        title={`${pageTitle} Classes`}
        description={seoDescription}
        path={programPath}
        ogImage={heroImage}
        ogImageAlt={`${pageTitle} gymnastics classes at Ace Gymnastics in Bardstown, KY`}
      />

      <ProgramPageHero
        displayName={currentProgram.fields.displayName}
        pageTitle={pageTitle}
        imageSrc={heroImage}
        imageAlt={`${pageTitle} at Ace Gymnastics`}
        ctaHref={
          showScheduleFilter && cat2Sections.length > 0
            ? `#${SCHEDULE_FILTER_HASH}`
            : "#program-schedules"
        }
      />

      <section
        className="home-section programs-page__intro"
        aria-labelledby="program-overview-heading"
      >
        <div className="home-section__inner">
          <ProgramSectionHeader
            id="program-overview-heading"
            kicker="What to expect"
            title="Why families love"
            titleAccent={currentProgram.fields.displayName}
          />
          <ProgramDescriptionBlock
            content={displayContent}
            variant="featured"
            imageSrc={heroImage}
            imageAlt={`${pageTitle} at Ace Gymnastics`}
          />
        </div>
      </section>

      {showScheduleFilter && cat2Sections.length > 0 && (
        <ClassScheduleFilter
          filter={scheduleFilter}
          onChange={setScheduleFilter}
          matchSummary={filterResults}
          hasLoadedSections={hasLoadedSections}
          isSettling={isFilterSettling}
          programName={programtype}
          ageMismatch={ageMismatch}
          filterUrgencySummary={filterUrgencySummary}
        />
      )}

      {showScheduleFilter && showFilterEmpty && (
        <ProgramsFilterEmpty
          programName={programtype}
          filter={settledFilter}
          filterEmptyReason={filterEmptyReason}
          onClearFilters={() => setScheduleFilter(DEFAULT_CLASS_SCHEDULE_FILTER)}
        />
      )}

      {cat2Sections.map((cat2, key) => (
        <div key={cat2.jackrabbitCat2Slug || key} id={key === 0 ? "program-schedules" : undefined}>
          <ProgramDetailCard
            count={key}
            sectionKey={cat2.jackrabbitCat2Slug || String(key)}
            jackrabbitSlug={currentProgram.fields.jackrabbitSlug}
            jackrabbitCat2={cat2.jackrabbitCat2Slug}
            cat2Description={cat2.description}
            categoryTitle={cat2.displayName}
            scheduleFilter={
              showScheduleFilter ? settledFilter : DEFAULT_CLASS_SCHEDULE_FILTER
            }
            onFilterResults={showScheduleFilter ? handleSectionResults : undefined}
            hideSchedule={showFilterEmpty}
          />
        </div>
      ))}
    </>
  );
};

export default ProgramDescription;
